import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { AppointmentStatusCell, DATE_FORMATS_FNS, Pin as PinComponent, ReactTable, TableDropdownSelector, TableQuestionStatusButton, useCan } from "components";
import { usePermanentLocations, usePermanentRealtimeDealers, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, MANUFACTURER_REQUEST_STATUSES, PIN_TYPES, WARRANTY_TYPES } from "models";
import { QuestionResultStatus } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { ManufacturerStatus } from "modules/ManufacturerDashboard/components";
import "modules/ManufacturerDashboard/components/ManufacturerTable/ManufacturerTable.scss";
import {
  setManufacturerDashboardFilters,
  useManufacturerDashboardFilters,
  useManufacturerDropdownOptions,
  useManufacturerFilterHandlers,
  useManufacturerRequestsQuery
} from "modules/ManufacturerDashboard/hooks";
import { MANUFACTURER_STORAGE_KEYS } from "modules/ManufacturerDashboard/storageKeys";
import { handleCopyToClipboard, validateManufacturerDropdownInput } from "modules/ManufacturerDashboard/utils";
import { LocationCard } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/components";
import { REG_VIN_FILTER } from "modules/WarrantyDashboard/hooks";
import { PATHS } from "router/paths";
import { setPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { ManufacturerRequest, Pin } from "models";

type ManufacturerTablePropTypes = {
  onSelectPinItem: (pin: Pin[] | null) => void;
  openRequestModal: (request: ManufacturerRequest) => void;
};

const columnHelper = createColumnHelper<ManufacturerRequest>();

export const ManufacturerTable = ({ openRequestModal, onSelectPinItem }: ManufacturerTablePropTypes) => {
  const { t } = useTranslation();

  const [activeRegFilter, setActiveRegFilter] = useState(REG_VIN_FILTER.Reg);

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { selectedLocation } = useSelectedLocation();
  const { data: brands = [] } = useCarBrandsQuery();
  const canUpdateRequest = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ManufacturerRequest);
  const { updatePinDropdownOptions, handlePaginationSelect, handlePageSizeChange } = useManufacturerFilterHandlers();
  const { pinStatusDropdownOptions } = useManufacturerDropdownOptions();
  const { manufacturerRequests, isFetching, refetch: refetchManufacturersRequests } = useManufacturerRequestsQuery();
  const { warrantyDropdownOptions, regHeaderOptions, questionStatusDropdownOptions, manufacturerRequestStatusDropdownOptions, pinTypeDropdownOptions } =
    useManufacturerDropdownOptions();
  const manufacturerFilters = useManufacturerDashboardFilters();

  useRegisterNavAction(refetchManufacturersRequests);

  const { warranty_type_id: warrantyTypeIdFilter, pin_type_id, status, request_status, page: currentPage, page_size: pageSize } = manufacturerFilters;

  const columns = [
    columnHelper.accessor(row => row, {
      id: "appointment_date",
      header: () => <span>{t("v8_appointment", "Appointment")}</span>,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length || !original.pin_history[0].appointment) return null;

        return <span>{formatDate(original.pin_history[0].appointment.created_on, DATE_FORMATS_FNS.dateMonthYear)}</span>;
      },
      size: 80
    }),
    columnHelper.accessor(row => row, {
      id: "appointment_status",
      header: () => <span>{t("v8_status", "Status")}</span>,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length || !original.pin_history[0].appointment) return null;

        return <AppointmentStatusCell value={{ id: original.pin_history[0].appointment.appointment_status_identifier }} statuses={selectedLocation?.statuses} />;
      },
      size: 30
    }),
    columnHelper.accessor(row => row, {
      id: "wo_nr",
      header: "WO #",
      size: 10,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length || !original.pin_history[0].appointment) return null;

        const { id, wo_nr } = original.pin_history[0].appointment;
        return (
          <Popup
            basic
            content={wo_nr}
            trigger={
              <div className="Manufacturer__tableColumn-openLink">
                {canUpdateRequest || original.status === MANUFACTURER_REQUEST_STATUSES.Approved ? (
                  <Link target="_blank" to={`${PATHS.APPOINTMENTS}/${id}`}>
                    <Icon className="external alternate" />
                    <p className="ellipsis">{wo_nr}</p>
                  </Link>
                ) : (
                  <p className="ellipsis">{wo_nr}</p>
                )}
              </div>
            }
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "reg_nr",
      size: 10,
      header: () => (
        <div className="Manufacturer__tableColumn-filters">
          <TableDropdownSelector
            options={regHeaderOptions}
            trigger={<span>{activeRegFilter === REG_VIN_FILTER.Reg ? `${t("v8_reg", "Reg")}` : `${t("v8_vin", "Vin")} `} #</span>}
            selected={[String(activeRegFilter)]}
            handleOnSelect={(selectedItem: string[]) => setActiveRegFilter(Number(selectedItem.at(0)))}
          />
        </div>
      ),
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length || !original.pin_history[0].appointment) return null;

        const { reg_number, vin_nr, car_id } = original.pin_history[0].appointment;
        const shownValue = activeRegFilter === REG_VIN_FILTER.Reg ? reg_number : vin_nr;

        if (!shownValue) return;

        const handleCopyVinNr = () =>
          activeRegFilter === REG_VIN_FILTER.Vin ? handleCopyToClipboard(shownValue, t("v8_vin_nr_copied", "Vin # copied to clipboard.")) : undefined;

        return (
          <div className="Manufacturer__tableColumn-openLink">
            {(canUpdateRequest || original.status === MANUFACTURER_REQUEST_STATUSES.Approved) && activeRegFilter === REG_VIN_FILTER.Reg ? (
              <Link target="_blank" to={`${PATHS.CAR_DETAIL}/${car_id}`}>
                <Icon className="external alternate" />
                <Popup basic content={shownValue} trigger={<p className="ellipsis">{shownValue}</p>} />
              </Link>
            ) : (
              <div className="Manufacturer__copyWrapper Manufacturer__copyWrapper--shortColumn" onClick={handleCopyVinNr}>
                {activeRegFilter === REG_VIN_FILTER.Vin && <Icon className="copy outline" />}
                <Popup basic content={shownValue} trigger={<p className="ellipsis">{shownValue}</p>} />
              </div>
            )}
          </div>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "dealer_id",
      header: t("v8_dealer", "Dealer"),
      size: 30,
      cell: ({
        row: {
          original: { dealer_id }
        }
      }) => {
        const dealer = dealers.find(dealer => dealer.id === dealer_id)?.name ?? "";
        return <Popup basic content={dealer} trigger={<p className="ellipsis">{dealer}</p>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "location_id",
      size: 30,
      header: t("v8_location", "Location"),
      cell: ({
        row: {
          original: { dealer_location_id }
        }
      }) => <span>{locations.find(location => location.id === dealer_location_id)?.name}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "pin_date",
      size: 50,
      header: t("v8_pin_date", "Pin Date"),
      cell: ({
        row: {
          original: { created_on }
        }
      }) => {
        const date = formatDate(created_on, DATE_FORMATS_FNS.dateMonthYear);
        return <Popup content={date} trigger={<p className="ellipsis">{date}</p>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "pin_type",
      size: 50,
      header: t("v8_pin_type", "Pin Type"),
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        return <span>{PIN_TYPES[original.pin_history[0].pin_type_id]}</span>; // TODO: we have to show translated value here
      }
    }),
    columnHelper.accessor(row => row, {
      id: "warranty_type",
      size: 100,
      header: () => {
        const selectedWarrantyDropdownOptions = warrantyTypeIdFilter != null ? [String(warrantyTypeIdFilter)] : ["all"];
        const popupText = t("v8_warranty", "Warranty");
        return (
          <div className="Manufacturer__tableColumn-filters">
            <TableDropdownSelector
              options={warrantyDropdownOptions}
              selected={selectedWarrantyDropdownOptions}
              trigger={<p className="ellipsis">{popupText}</p>}
              handleOnSelect={(selectedItem: string[]) => {
                const validatedManufacturerDropdownInput = validateManufacturerDropdownInput(selectedItem);
                setPreference(MANUFACTURER_STORAGE_KEYS.WarrantyPreference, validatedManufacturerDropdownInput);
                setManufacturerDashboardFilters({ ...manufacturerFilters, warranty_type_id: validatedManufacturerDropdownInput, page: 1 });
              }}
            />
          </div>
        );
      },

      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const { warranty_type_id } = original.pin_history[0];
        if (warrantyTypeIdFilter === null && warranty_type_id === WARRANTY_TYPES.NotSet) return null;

        return <span>{warrantyDropdownOptions.find(option => option.value === warranty_type_id)?.text ?? ""}</span>;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "pin_status",
      size: 30,
      header: t("v8_pin_status", "Pin Status"),
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const { pin_status_id } = original.pin_history[0];
        const pinStatus = pinStatusDropdownOptions.find(option => option.value === pin_status_id)?.text ?? "";

        return <Popup basic content={pinStatus} trigger={<p className="ellipsis">{pinStatus}</p>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "support_nr",
      size: 5,
      header: `${t("v8_support", "Support")} #`,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const { support_nr } = original.pin_history[0];
        if (!support_nr) return;

        return (
          <div className="Manufacturer__copyWrapper" onClick={() => handleCopyToClipboard(support_nr, t("v8_support_nr_copied", "Support # copied to clipboard"))}>
            <Icon className="copy outline" />
            <Popup basic content={support_nr} trigger={<p className="ellipsis Manufacturer__short-column">{support_nr}</p>} />
          </div>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "claim_nr",
      size: 5,
      header: `${t("v8_claim", "Claim")} #`,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const { claim_nr } = original.pin_history[0];
        if (!claim_nr) return;

        return (
          <div className="Manufacturer__copyWrapper" onClick={() => handleCopyToClipboard(claim_nr, t("v8_claim_nr_copied", "Claim # copied to clipboard"))}>
            <Icon className="copy outline" />
            <Popup basic content={claim_nr} trigger={<p className="ellipsis  Manufacturer__short-column">{claim_nr}</p>} />
          </div>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "ref_nr",
      size: 5,
      header: `${t("v8_ref", "Ref ")} #`,
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const { ref_nr } = original.pin_history[0];
        if (!ref_nr) return;

        return (
          <div className="Manufacturer__copyWrapper" onClick={() => handleCopyToClipboard(ref_nr, t("v8_claim_nr_copied", "Ref # copied to clipboard"))}>
            <Icon className="copy outline" />
            <Popup basic content={ref_nr} trigger={<p className="ellipsis  Manufacturer__short-column">{ref_nr}</p>} />
          </div>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "brand",
      size: 40,
      header: t("v8_brand", "Brand"),
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length || !original.pin_history[0].appointment?.car) return null;

        const { make } = original.pin_history[0].appointment.car;
        return <Popup basic content={make} trigger={<p className="ellipsis  Manufacturer__short-column">{make}</p>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "intervention_question",
      size: 100,
      header: () => {
        const value = t("v8_intervention_question", "Intervention/Question");
        return <Popup content={value} trigger={<p className="ellipsis">{value}</p>} />;
      },
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const title = original.pin_history[0].intervention?.title || original.pin_history[0].question_result?.title;
        return title ? <Popup basic content={title} trigger={<p className="ellipsis">{title}</p>} /> : null;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "actions",
      size: 40,
      header: () => {
        const selectedPinClaimsOptions = pin_type_id?.length ? pin_type_id.map(id => String(id)) : ["all"];
        const selectedManufacturerStatusOptions = status ? [String(status)] : ["all"];
        const selectedManufacturerRequestStatusOptions = request_status ? [String(request_status)] : ["all"];

        return (
          <div className="Manufacturer__tableColumn-actions">
            <div></div>
            <TableDropdownSelector
              selected={selectedPinClaimsOptions}
              multiple
              options={pinTypeDropdownOptions}
              handleOnSelect={selectedValue => updatePinDropdownOptions(selectedValue)}
              trigger={<TableQuestionStatusButton selectedQuestionStatuses={selectedPinClaimsOptions} />}
              scrolling={false}
              showFilterIcon={false}
            />
            <TableDropdownSelector
              selected={selectedManufacturerStatusOptions}
              options={questionStatusDropdownOptions}
              handleOnSelect={(selectedValue: string[]) => {
                const validatedManufacturerDropdownInput = validateManufacturerDropdownInput(selectedValue);
                setPreference(MANUFACTURER_STORAGE_KEYS.QuestionStatusPreference, validatedManufacturerDropdownInput);
                setManufacturerDashboardFilters({ ...manufacturerFilters, status: validatedManufacturerDropdownInput, page: 1 });
              }}
              trigger={<TableQuestionStatusButton selectedQuestionStatuses={selectedManufacturerStatusOptions} />}
              scrolling={false}
              className="Manufacturer__tableColumn-actions--statusId"
              showFilterIcon={false}
            />
            <TableDropdownSelector
              selected={selectedManufacturerRequestStatusOptions}
              options={manufacturerRequestStatusDropdownOptions}
              handleOnSelect={(selectedValue: string[]) => {
                const validatedManufacturerDropdownInput = validateManufacturerDropdownInput(selectedValue);
                setPreference(MANUFACTURER_STORAGE_KEYS.RequestStatusPreference, validatedManufacturerDropdownInput);
                setManufacturerDashboardFilters({ ...manufacturerFilters, request_status: validateManufacturerDropdownInput(selectedValue), page: 1 });
              }}
              trigger={<TableQuestionStatusButton selectedQuestionStatuses={selectedManufacturerRequestStatusOptions} />}
              scrolling={false}
              showFilterIcon={false}
              className="Manufacturer__tableColumn-actions--requestStatus"
            />
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        if (!original.pin_history?.length) return null;

        const latestPin = original.pin_history[0];
        const questionResultStatus = latestPin.question_result?.status ?? latestPin.intervention?.question_result_status ?? null;
        const location = useAppointmentLocation(latestPin.appointment);

        return (
          <div className="Manufacturer__tableColumn-actions Manufacturer__tableColumn-actions--cell">
            <LocationCard dealerLocationId={original.dealer_location_id} dealers={dealers} locations={locations} />
            {latestPin.appointment && location ? (
              <PinComponent
                pin={{ ...latestPin, history: original.pin_history }}
                vin={latestPin.appointment.vin_nr}
                location={location}
                brands={brands}
                intervention={latestPin.intervention}
                questionResult={latestPin.question_result}
                onClick={() => onSelectPinItem(original.pin_history ?? null)}
              />
            ) : (
              <div />
            )}
            {questionResultStatus ? (
              <div className="Manufacturer__table-action-buttons-container">
                <QuestionResultStatus status={questionResultStatus} />
              </div>
            ) : (
              <div />
            )}
            <ManufacturerStatus request={original} openRequestModal={openRequestModal} />
          </div>
        );
      }
    })
  ];

  return (
    <ReactTable
      isFetching={isFetching}
      pageCount={manufacturerRequests?.nb_pages ?? 0}
      data={manufacturerRequests?.manufacturer_requests}
      columns={columns}
      totalItems={manufacturerRequests?.nb_items}
      pageSize={pageSize}
      pagination={(manufacturerRequests?.nb_pages ?? 0) > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
      onPaginationChange={handlePaginationSelect}
      onChangePageSize={handlePageSizeChange}
    />
  );
};
